// ReviewCard component
"use client";

import React from 'react';
import { AiOutlineLink } from 'react-icons/ai';
import { faGoogle, faFacebook } from '@fortawesome/free-brands-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { Badge } from '../ui/badge';
import Image from 'next/image';
import { Rating, ThinStar } from '@smastrom/react-rating';
import { absoluteUrl, shadeColor } from '@/lib/utils';
import Link from 'next/link';
import { Icons } from '@/components/icons';
import { SSIcon } from '@/components/ui/ss-icon';
import { cn } from '@/lib/utils'
import { buttonVariants } from '../ui/button';
import { determineTextColor } from '@/lib/utils';
import { ExtendedUserReview } from '@/types/review';
import { decode } from 'html-entities';
import { Avatar } from '@/components/avatar';

interface ReviewCardProps {
  review: ExtendedUserReview;
  hexColour: string;
  primaryBackgroundColour?: string;
  cardStyle?: string | null;
  isLocationProfile?: boolean;
  isAdmin?: boolean;
  isBlurred?: boolean;
  className?: string;
  hideBadge?: boolean;
}

const getCardStyle = (style: string | null) => {
  switch (style) {
    case 'plain':
      return 'bg-white rounded-xl drop-shadow-xl';
    case 'border':
      return 'bg-white rounded-xl border border-zinc-800';
    case 'shadow':
      return 'bg-white rounded-xl shadow-[5px_5px_0px_2px_#2E2E2E]';
    case 'transparent':
      return 'bg-white bg-opacity-20 rounded-xl shadow';
    default:
      return 'bg-white rounded-xl drop-shadow-xl';
  }
};

const ReviewCard: React.FC<ReviewCardProps> = ({ review, hexColour, primaryBackgroundColour, cardStyle, isLocationProfile, isAdmin, isBlurred, className, hideBadge }) => {
  const { user, rating, title, text, name, date, origin, createdAt } = review;

  const myStyles = {
    itemShapes: ThinStar,
    activeFillColor: hexColour,
    inactiveFillColor: shadeColor(hexColour, 50)
  };

  let textColour = ''
  if (cardStyle === 'transparent' && primaryBackgroundColour) {
    textColour = determineTextColor(primaryBackgroundColour);
  }

  const getOriginIcon = (origin: string | null) => {
  
    switch (origin) {
      case 'facebook':
        return <FontAwesomeIcon style={{ color: hexColour }} className={`w-5 h-7 mx-2`} icon={faFacebook as IconProp} />;
      case 'google':
        return <FontAwesomeIcon style={{ color: hexColour }} className={`w-5 h-7 mx-2`} icon={faGoogle as IconProp} />;
      case 'superstack':
        return <SSIcon fill={hexColour} width={30} height={30} />;
      default:
        return <AiOutlineLink className="text-gray-500" />;
    }
  };
  

  let ratingValue = rating || 0;
  let dateValue = date ? new Date(date) : new Date(createdAt);
  // Only show the badge for reviews created in the last 7 days
  let showBadge = !hideBadge && (new Date().getTime() - dateValue.getTime()) / (1000 * 3600 * 24) < 7;

  return (
    <div className={cn("w-full", className)}>
      <div className={`p-6 ${ (!isAdmin && cardStyle) ? getCardStyle(cardStyle) : 'bg-white rounded-xl drop-shadow-xl'} flex flex-col justify-between sm:w-full sm:mx-0`}>
        <div>
          {/* isLocationProfile AND user.urlSlug is not null AND user.name is not null */}
          {isLocationProfile && !isAdmin && user.urlSlug && user.name && (
            <Link
              className="flex items-center bg-white rounded-lg border-gray-200 border p-2 my-3 w-full transition-transform duration-450 ease-in-out transform hover:scale-105"
              href={absoluteUrl(user.urlSlug)}
            >
              <div className="flex items-center space-x-3">
                {user.profileImageUrl ? (
                  <div className="relative rounded-full overflow-hidden w-12 h-12">
                    <Image 
                      src={user.profileImageUrl} 
                      alt={user.name || 'Profile image'} 
                      width={128} 
                      height={128} 
                      className="absolute inset-0 w-full h-full object-cover" 
                    />
                  </div>
                ) : (
                  <Avatar name={user.name || ''} size="sm" className="w-12 h-12" colorise />
                )}
                <div>
                  <p className="text-sm text-gray-500">Review for</p>
                  <div className="flex flex-row">
                    <h3 className="text-md font-semibold">{user.name}</h3>
                    { user.isPremium && (
                      <Image className="ml-1" src="/super-icon.svg" alt="Super tick" width={18} height={18} />
                    )}
                  </div>
                </div>
              </div>
              <div className="flex-grow" />
              <div>
                <Icons.chevronRight />
              </div>
            </Link>
          )}
          <div className="flex flex-row items-center">
            <div className="flex w-1/2 my-2 mr-2">
              <Rating
                className="mt-1"
                style={{ maxWidth: 250 }}
                value={ratingValue}
                readOnly
                halfFillMode='svg'
                itemStyles={myStyles}
              />
            </div>
            {showBadge && <Badge style={{ backgroundColor: hexColour }} className={`rounded-none mt-1 filter-none`}>NEW</Badge>}
          </div>
          <div style={{ color: textColour }} className="text-xl font-medium mb-2">{decode(title)}</div>
          <p style={{ color: textColour ? textColour: '#6B7280'}} className="mb-2">{decode(text)}</p>
          <div className="mt-4 flex items-center">
            { review.reviewerUser && review.reviewerUser.profileImageUrl && 
              <div className="relative rounded-full overflow-hidden w-10 h-10 mr-4">
                <Image src={review.reviewerUser.profileImageUrl} alt={review.reviewerUser.name || 'Profile image'} width={128} height={128} className="absolute inset-0 w-full h-full object-cover rounded-full" />
              </div>
            }
            <div className="flex flex-col">
              <div style={{ color: textColour }} className="font-medium">{name}</div>
              <div style={{ color: textColour ? textColour: '#6B7280'}} className="text-xs">{new Date(dateValue).toLocaleDateString('en-GB')}</div>
            </div>
            <div className="flex-grow" />
            <div className="self-end">
              {getOriginIcon(origin)}
            </div>
          </div>
          { isAdmin && !isBlurred &&
          <div className="mt-5">
            <Link className={cn(buttonVariants(), "bg-blue-500 w-1/4 min-w-[100px]")} href={`/review-share?id=${review.id}`}>
              Share
            </Link>
          </div>
          }
        </div>
      </div>
    </div>
  );
};

export default ReviewCard;

